import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import HotelCard from './HotelCard';
import RestaurantCard from './RestaurantCard';
import BudgetSummary from './BudgetSummary';
import AlternativesModal from './AlternativesModal';

const TripPlanResults = ({ tripPlan, onReset, originalBudget }) => {
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedRestaurants, setSelectedRestaurants] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [showAlternativesModal, setShowAlternativesModal] = useState(false);
  const [alternativeType, setAlternativeType] = useState(null);
  const [alternativeContext, setAlternativeContext] = useState(null);

  const tripDetails = {
    destination: tripPlan?.destination,
    startDate: new Date()?.toISOString()?.split('T')?.[0],
    endDate: new Date(Date.now() + (tripPlan?.totalDays || 5) * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
    travelers: 2,
    totalDays: tripPlan?.totalDays
  };

  useEffect(() => {
    if (tripPlan) {
      const initialHotels = tripPlan?.hotels?.filter(h => h?.isSelected) || [];
      const initialRestaurants = tripPlan?.restaurants?.filter(r => r?.isSelected) || [];
      setSelectedHotels(initialHotels);
      setSelectedRestaurants(initialRestaurants);
    }
  }, [tripPlan]);

  useEffect(() => {
    const hotelCost = selectedHotels?.reduce((sum, hotel) => sum + (hotel?.totalCost || 0), 0);
    const restaurantCost = selectedRestaurants?.reduce((sum, restaurant) => sum + (restaurant?.totalCost || 0), 0);
    setTotalCost(hotelCost + restaurantCost);
  }, [selectedHotels, selectedRestaurants]);

  const handleShowAlternatives = (type, currentItem) => {
    setAlternativeType(type);
    setAlternativeContext(currentItem);
    setShowAlternativesModal(true);
  };

  const handleSelectAlternative = (type, newItem, oldItem) => {
    if (type === 'hotel') {
      setSelectedHotels(prev => 
        prev?.map(hotel => hotel?.id === oldItem?.id ? { ...newItem, isSelected: true } : hotel)
      );
    } else if (type === 'restaurant') {
      setSelectedRestaurants(prev => 
        prev?.map(restaurant => restaurant?.id === oldItem?.id ? { ...newItem, isSelected: true } : restaurant)
      );
    }
    setShowAlternativesModal(false);
  };

  const budgetStatus = {
    remaining: originalBudget - totalCost,
    percentage: (totalCost / originalBudget) * 100,
    isOverBudget: totalCost > originalBudget
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Your {tripPlan?.totalDays}-Day Trip to {tripPlan?.destination}
          </h2>
          <p className="text-muted-foreground">{tripPlan?.summary}</p>
        </div>
        <Button
          variant="outline"
          iconName="RotateCcw"
          onClick={onReset}
        >
          Plan New Trip
        </Button>
      </div>
      {/* Budget Summary */}
      <BudgetSummary
        originalBudget={originalBudget}
        totalCost={totalCost}
        budgetStatus={budgetStatus}
      />
      {/* Hotels Section */}
      <div className="bg-card rounded-2xl shadow-lg p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="Hotel" size={28} className="text-[#2E8B8B]" />
          <h3 className="text-2xl font-bold text-foreground">Accommodation</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {selectedHotels?.map((hotel) => (
            <HotelCard
              key={hotel?.id}
              hotel={hotel}
              onShowAlternatives={() => handleShowAlternatives('hotel', hotel)}
              tripDetails={tripDetails}
            />
          ))}
        </div>
      </div>
      {/* Restaurants Section */}
      <div className="bg-card rounded-2xl shadow-lg p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <Icon name="UtensilsCrossed" size={28} className="text-[#F4A261]" />
          <h3 className="text-2xl font-bold text-foreground">Dining Recommendations</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {selectedRestaurants?.map((restaurant) => (
            <RestaurantCard
              key={restaurant?.id}
              restaurant={restaurant}
              onShowAlternatives={() => handleShowAlternatives('restaurant', restaurant)}
              tripDetails={tripDetails}
            />
          ))}
        </div>
      </div>
      {/* Alternatives Modal */}
      {showAlternativesModal && (
        <AlternativesModal
          isOpen={showAlternativesModal}
          onClose={() => setShowAlternativesModal(false)}
          type={alternativeType}
          currentItem={alternativeContext}
          alternatives={alternativeType === 'hotel' ? tripPlan?.alternatives?.hotels : tripPlan?.alternatives?.restaurants}
          onSelect={(newItem) => handleSelectAlternative(alternativeType, newItem, alternativeContext)}
        />
      )}
    </div>
  );
};

export default TripPlanResults;